import * as React from "react";
import debounce from "lodash.debounce";
import styled from "styled-components";
import { StoreContext } from "../../provider/StoreProvider";
import { formatPrice } from "../../utils/format-price";
import { GatsbyImage } from "gatsby-plugin-image";
import { getShopifyImage } from "gatsby-source-shopify";
import { NumericInput } from "./numeric-input.js";
import { variant, totals } from "./line-item.module.css";
import CloseIcon from "../../assets/store/closeicon.svg";

const CloseTd = styled.td`
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  padding-left: 0px !important;
  padding-top: 15px;
  @media only screen and (max-width: 840px) {
    display: flex;
    padding-right: 5px;
    align-items: center;
    font-size: 14px;
    padding-left: 0px;
    border-bottom: 0px;
    padding-top: 16px !important;
  }
`;

const MobileCloseTd = styled.td`
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  padding-left: 0px !important;
  @media only screen and (max-width: 840px) {
    display: flex;
    padding-right: 5px;
    align-items: center;
    font-size: 14px;
    padding-left: 0px;
    padding-top: 0px;
    border-bottom: 0.5px solid black;
  }
`;

const ItemTd = styled.td`
  width: 112px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  padding-top: 15px !important;
  padding-bottom: 24px !important;
  @media only screen and (max-width: 840px) {
    font-size: 14px;
  }
  @media only screen and (max-width: 480px) {
    width: 80px;
    align-items: flex-start;
    height: fit-content;
    padding: 8px !important;
    padding-top: 0px !important;
    border-bottom: 0px !important;
    padding-top: 16px !important;
  }
`;

const MobileItemTd = styled.td`
  width: 112px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 840px) {
    width: 80px;
    font-size: 14px;
  }
  @media only screen and (max-width: 480px) {
    align-items: flex-start;
    height: fit-content;
    padding: 8px !important;
    padding-top: 0px !important;
    border-bottom: 0.5px solid black;
  }
`;

const NumericInputTd = styled.td`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 840px) {
    padding-left: 15px;
    padding-right: 0px;
    font-size: 14px;
    padding-top: 16px !important;
  }
  @media only screen and (max-width: 480px) {
    border-bottom: 0px;
  }
`;

const MobileNumericInputTd = styled.td`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 840px) {
    padding-left: 15px;
    padding-right: 0px;
    font-size: 14px;
    border-bottom: 0.5px solid black;
  }
`;

const TitleTd = styled.td`
  width: 250px;
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 840px) {
    padding: 0px;
    font-size: 14px;
    width: 100%;
  }
  @media only screen and (max-width: 480px) {
    display: none;
  }
`;

const DesktopH2 = styled.h2`
  font-size: 16px !important;
  font-weight: 300;
  text-transform: uppercase;
  @media only screen and (max-width: 480px) {
    width: 70px;
    font-size: 14px !important;
  }
`;

const MobileH2 = styled.h2`
  font-size: 18px;
  font-weight: 300;
  display: none;
  @media only screen and (max-width: 480px) {
    display: block;
    width: fit-content;
    margin-top: 0px;
    margin-bottom: 16px;
    font-size: 14px !important;
  }
`;

const SubTotalTd = styled.td`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 480px) {
    padding-left: 2px;
    display: flex;
    justify-content: flex-end;
    font-size: 16px;
    padding-right: 0px;
    border-bottom: 0px;
    padding-top: 16px !important;
  }
`;

const MobileSubTotalTd = styled.td`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-bottom: 0.5px solid black;
  margin: 0px;
  @media only screen and (max-width: 480px) {
    padding-left: 2px;
    display: flex;
    justify-content: flex-end;
    font-size: 16px;
    padding-right: 0px;
    border-bottom: 0.5px solid black;
  }
`;

const MobileTr = styled.tr`
  display: none;
  @media only screen and (max-width: 480px) {
    display: contents !important;
    border-bottom: 0.5px solid black;
    height: 40px;
    padding-top: 0px;
  }
`;

export function LineItem({ item }) {
  const {
    removeLineItem,
    checkout,
    updateLineItem,
    loading
  } = React.useContext(StoreContext);
  const [quantity, setQuantity] = React.useState(item.quantity);

  const variantImage = {
    ...item.variant.image,
    originalSrc: item.variant.image.src
  };
  // const price = formatPrice(
  //   item.variant.priceV2.currencyCode,
  //   Number(item.variant.priceV2.amount)
  // )

  const subtotal = formatPrice(
    item.variant.priceV2.currencyCode,
    Number(item.variant.priceV2.amount) * quantity
  );

  const handleRemove = () => {
    removeLineItem(checkout.id, item.id);
  };

  const uli = debounce(
    value => updateLineItem(checkout.id, item.id, value),
    300
  );
  // eslint-disable-next-line
  const debouncedUli = React.useCallback(value => uli(value), []);

  const handleQuantityChange = value => {
    if (value !== "" && Number(value) < 1) {
      return;
    }
    setQuantity(value);
    if (Number(value) >= 1) {
      debouncedUli(value);
    }
  };

  function doIncrement() {
    handleQuantityChange(Number(quantity || 0) + 1);
  }

  function doDecrement() {
    handleQuantityChange(Number(quantity || 0) - 1);
  }

  const image = React.useMemo(
    () =>
      getShopifyImage({
        image: variantImage,
        layout: "constrained",
        crop: "contain",
        width: 160,
        height: 160
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variantImage.src]
  );

  return (
    <>
      <tr
        style={{
          paddingTop: "24px",
          borderBottom: "0.5px solid black",
          height: "40px",
          display: "contents"
        }}
      >
        <CloseTd>
          <button
            onClick={handleRemove}
            style={{ padding: "5px", display: "flex", alignItems: "center" }}
          >
            <img src={CloseIcon} size={20} alt="thumbnails" />
          </button>
        </CloseTd>
        <ItemTd>
          {image && (
            <GatsbyImage
              key={variantImage.src}
              image={image}
              alt={variantImage.altText ?? item.variant.title}
            />
          )}
        </ItemTd>
        <TitleTd>
          <DesktopH2>{item.title}</DesktopH2>
          <div className={variant}>
            {item.variant.title === "Default Title" ? "" : item.variant.title}
          </div>
        </TitleTd>
        <NumericInputTd>
          <NumericInput
            disabled={loading}
            value={quantity}
            aria-label="Quantity"
            onIncrement={doIncrement}
            onDecrement={doDecrement}
            onChange={e => handleQuantityChange(e.currentTarget.value)}
          />
        </NumericInputTd>
        <SubTotalTd className={totals}>{subtotal}</SubTotalTd>
      </tr>
      <MobileTr className="mobiletr">
        <MobileCloseTd />
        <MobileItemTd>
          <MobileH2>{item.title}</MobileH2>
        </MobileItemTd>
        <TitleTd />
        <MobileNumericInputTd />
        <MobileSubTotalTd className={totals}></MobileSubTotalTd>
      </MobileTr>
    </>
  );
}
